import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { reorderLayers, setActiveLayerId, setActiveRegionId } from "../../reducers/editorSlice";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface LayersListProps {
  onDelete?: (layerId: string) => void;
}

function canDeleteLayer(layerType: string) {
  return layerType !== "background";
}

function layerTypeLabel(layerType: string) {
  if (layerType === "text") return "Text";
  if (layerType === "design") return "Design";
  if (layerType === "region") return "Region";
  if (layerType === "background") return "Background";
  return "Image";
}

function isPinnedLayerType(layerType: string) {
  return layerType === "design" || layerType === "region" || layerType === "background";
}

const LayersList: React.FC<LayersListProps> = ({ onDelete }) => {
  const dispatch = useDispatch();
  const layers = useSelector((state: RootState) => state.editor.layers);
  const regions = useSelector((state: RootState) => state.editor.regions);
  const activeLayerId = useSelector((state: RootState) => state.editor.activeLayerId);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const visualLayers = [...layers].filter((layer) => layer.layerType !== "background").reverse();

  const handleSelect = (layerId: string) => {
    dispatch(setActiveLayerId(layerId));
    if (regions.some((r) => r.id === layerId)) {
      dispatch(setActiveRegionId(layerId));
    }
    window.dispatchEvent(new CustomEvent("select-fabric-object", { detail: { layerId } }));
  };

  const handleDelete = (layerId: string) => {
    if (onDelete) {
      onDelete(layerId);
      return;
    }
    window.dispatchEvent(new CustomEvent("remove-fabric-object", { detail: { layerId } }));
  };

  const handleToggleVisible = (e: React.MouseEvent, layerId: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent("toggle-fabric-visibility", { detail: { layerId } }));
  };

  const handleDropOn = (targetId: string, fromId?: string | null) => {
    const sourceId = fromId || dragId;
    if (!sourceId || sourceId === targetId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const sourceLayer = visualLayers.find((layer) => layer.layerId === sourceId);
    if (!sourceLayer || isPinnedLayerType(sourceLayer.layerType)) {
      setDragId(null);
      setOverId(null);
      return;
    }

    const movableVisual = visualLayers.filter((layer) => !isPinnedLayerType(layer.layerType));
    const pinned = layers.filter((layer) => isPinnedLayerType(layer.layerType));
    const from = movableVisual.findIndex((layer) => layer.layerId === sourceId);
    let to = movableVisual.findIndex((layer) => layer.layerId === targetId);
    if (to < 0) to = movableVisual.length - 1;
    if (from < 0 || to < 0 || from === to) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const nextMovable = [...movableVisual];
    const [moved] = nextMovable.splice(from, 1);
    nextMovable.splice(to, 0, moved);
    const order = [
      ...pinned.map((layer) => layer.layerId),
      ...nextMovable.slice().reverse().map((layer) => layer.layerId),
    ];
    dispatch(reorderLayers(order));
    window.dispatchEvent(new CustomEvent("reorder-fabric-layers", { detail: { order } }));
    setDragId(null);
    setOverId(null);
  };

  return (
    <div className="layers_list">
      {visualLayers.map((layer) => {
        const hidden = layer.visible === false;
        return (
          <div
            key={layer.layerId}
            role="button"
            tabIndex={0}
            className={`layer_item ${activeLayerId === layer.layerId ? "active_layer" : ""} ${hidden ? "is-hidden" : ""} ${overId === layer.layerId ? "drag-over" : ""} ${dragId === layer.layerId ? "is-dragging" : ""}`}
            onClick={() => handleSelect(layer.layerId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(layer.layerId);
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (overId !== layer.layerId) setOverId(layer.layerId);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDropOn(layer.layerId, e.dataTransfer.getData('text/plain') || dragId);
            }}
            onDragLeave={() => {
              if (overId === layer.layerId) setOverId(null);
            }}
          >
            <div className="layer_left">
              <span
                className="drag_handle"
                draggable={!isPinnedLayerType(layer.layerType)}
                title={isPinnedLayerType(layer.layerType) ? "Base design stays behind overlays" : "Drag to reorder"}
                onClick={(e) => e.stopPropagation()}
                onDragStart={(e) => {
                  if (isPinnedLayerType(layer.layerType)) {
                    e.preventDefault();
                    return;
                  }
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", layer.layerId);
                  setDragId(layer.layerId);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 14 }} />
              </span>
              {layer.layerType === "text" ? (
                <div className="thumb text_thumb">T</div>
              ) : (
                <img
                  src={layer.layerType === "background" ? "/static/images/leaf.svg" : "/static/images/avtar.svg"}
                  alt=""
                  className="thumb"
                  style={layer.layerType === "background" ? { background: "#222" } : {}}
                />
              )}
              <div className="info">
                <strong>{layer.layerName}</strong>
                <span>{layerTypeLabel(layer.layerType)}</span>
              </div>
            </div>
            <div className="layer_right">
              {canDeleteLayer(layer.layerType) && (
                <button
                  type="button"
                  className="layer_delete_btn"
                  title={`Delete ${layer.layerName}`}
                  aria-label={`Delete ${layer.layerName}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(layer.layerId);
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </button>
              )}
              <button
                type="button"
                className="layer_vis_btn"
                title={hidden ? "Show layer" : "Hide layer"}
                onClick={(e) => handleToggleVisible(e, layer.layerId)}
              >
                {hidden ? (
                  <VisibilityOffIcon sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 18 }} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LayersList;
